import type { Transaction } from "@rocicorp/zero";
import { zql } from "../zql";

export namespace Users {
	/**
	 * Resolve the ranking that appends a person below a crew's current staffing.
	 *
	 * Mirrors the ticket path's `getNextRanking`: one above the crew's highest ranking is enough,
	 * because every staffing mutation ends in `Crews.cleanCrewStaffRankings`, which re-spaces the
	 * sequence onto 0, 100, 200 … before the next caller reads it.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param crewId - The crew the person is being added to.
	 * @returns A ranking that sorts after every person already in the crew.
	 */
	export async function getNextStaffRanking(tx: Transaction, crewId: string) {
		const last = await tx.run(
			zql.userAssignments.where("crewId", "=", crewId).orderBy("ranking", "desc").one(),
		);
		if (!last) return 0;
		return last.ranking + 1;
	}

	export async function cleanUserAssignments(tx: Transaction, planId: string, userId: string) {
		const crews = await tx.run(
			zql.crews
				.where("planId", "=", planId)
				.related("userAssignments", (userAssignment) =>
					userAssignment.where("userId", "=", userId).one(),
				),
		);

		for (const crew of crews) {
			if (crew.userAssignments) {
				await tx.mutate.userAssignments.delete({ id: crew.userAssignments.id });
			}
		}

		const vehicleAssignmentsPlan = await tx.run(
			zql.vehicleAssignmentsPlan.where("planId", "=", planId),
		);

		for (const vap of vehicleAssignmentsPlan) {
			if (vap.userId !== userId) continue;
			await tx.mutate.vehicleAssignmentsPlan.update({
				userId: null,
				id: vap.id,
			});
		}
	}
}
